import { TEntityId } from './../../types/models'
import { getKitItems } from './KitService'
import { ProductTypeAndLevelDescr } from './ProductTypeAndLevelDescr'
import type {
  IKitProduct,
  TProduct,
  IProductKitItem,
} from '~/types/product/models'
import { isKitProduct, isSetKitProduct } from '~/types/product/models'
import { formatNumber } from '~/cynqueModules/formatNumber'

const mn = '[cynqueModules/dataObjects/Product.ts]'

export function Product(o: TProduct): TProduct {
  const fn = '[cynqueModules/dataObjects/Product.ts] Product |'
  if (!o) {
    throw new Error(`${fn} Product called with falsey product`)
  }

  const obj = { ...o }

  obj.descrAndProductHrid = obj.descr + '\n' + obj.hrid
  obj.numOpportunitiesAndNumServicesDescr =
    //   obj.numOpportunitites + '\n' + obj.numServices
    `${formatNumber(obj.numOpportunities ?? '-')}\n${formatNumber(
      obj.numServices ?? '-'
    )}`

  obj.typeAndLevelDescr = ProductTypeAndLevelDescr(obj)

  let lateLaterDescr = obj.numDaysToLateAmber
    ? obj.numDaysToLateAmber.toString()
    : ''
  lateLaterDescr =
    lateLaterDescr && obj.numDaysToLateRed
      ? lateLaterDescr + '\n' + obj.numDaysToLateRed
      : lateLaterDescr ||
        (obj.numDaysToLateRed ? obj.reallyLaterAfterNumDays.toString() : '')

  obj.lateReallyLateDescr = lateLaterDescr

  if (obj.numDaysToLateAmber || obj.numDaysToLateRed) {
  }

  return obj
}

// // (Recursion head)
// export async function getProductsForKitProduct({
//   kitProduct,
// }: {
//   kitProduct: IKitProduct
// }) {
//   const fn = `${mn} getProductsForKitProduct |`
//   const theseProductsForKitItems: TProduct[] = []

//   if (!kitProduct.doExcludeKitServiceWhenBuildingServices) {
//     theseProductsForKitItems.push(kitProduct)
//   }

//   console.debug('***************')
//   console.debug(`${fn} Called with:`)
//   console.debug(`${fn} theseProductsForKitItems:`, theseProductsForKitItems)
//   console.debug(`${fn} kitProduct: `, kitProduct)
//   console.debug(
//     `${fn} kitProduct.doExcludeKitServiceWhenBuildingServices:`,
//     kitProduct.doExcludeKitServiceWhenBuildingServices
//   )
//   console.debug('***************')

//   const kitItems = await getKitItems({
//     callingMn: fn,
//     product: kitProduct.product,
//   })
//   for (const [i, item] of kitItems.entries()) {
//     if (!isKitProduct(item.product)) {
//       theseProductsForKitItems.push(item.product)
//     } else {
//       console.debug(
//         `${fn} Just hit a kit product within a kit (${item.product.descr}).`
//       )
//       theseProductsForKitItems.push(
//         ...(await getProductsForKitProduct({
//           kitProduct: item.product,
//         }))
//       )
//     }
//   }
//   console.debug(`${fn} returning theseProductsForKitItems...`)
//   console.dir(theseProductsForKitItems)
//   return theseProductsForKitItems
// }

export async function getProductKitItemsForKitProductDeep({
  kitProduct,
  parentOptionUid,
}: {
  kitProduct: IKitProduct
  // The nearest enclosing OPTION -- see the Set-inside-a-Set note below.
  parentOptionUid?: string
}) {
  return (
    await getProductKitItemsForKitProductDeepWithContent({
      kitProduct,
      parentOptionUid,
    })
  ).items
}

/**
 * The same walk, plus whether the kit builds a row whatever is picked -- a
 * mandatory item, a mandatory plain Kit (whose header is always built), or a
 * mandatory Set's mandatory member. Each optional Kit or Set listed carries
 * the same answer for itself as `_hasContent`. Both feed the "Select at least
 * one option" checks (cynqueModules/kitOptionContent.ts), which have no
 * supplier-product to read them from here.
 */
// (Recursion Head)
export async function getProductKitItemsForKitProductDeepWithContent({
  kitProduct,
  parentOptionUid,
}: {
  kitProduct: IKitProduct
  parentOptionUid?: string
}): Promise<{ items: IProductKitItem[]; hasUnconditionalContent: boolean }> {
  const fn = `${mn} getProductKitItemsForKitProductDeepWithContent |`
  const theseProductKitItemsForKitItems: IProductKitItem[] = []
  let hasUnconditionalContent = false

  console.debug('***************')
  console.debug(`${fn} Called with...`)
  console.debug(
    `${fn} theseProductKitItemsForKitItems:`,
    theseProductKitItemsForKitItems
  )
  console.debug(`${fn} kitProduct: `, kitProduct)
  console.debug('***************')

  const isParentASet = isSetKitProduct(kitProduct)
  const kitItems = await getKitItems({
    callingMn: fn,
    product: kitProduct,
  })
  // A catalogue-only option has no supplier-product to read the Set's pick
  // bounds from (see onePerSectionSelect), so carry the owning kit's.
  function _tagAsOption(item: IProductKitItem) {
    ;(item as any)._isFromSet = isParentASet
    ;(item as any)._doAllowNoSelectionFromSet =
      isParentASet && !!kitProduct.doAllowNoSelectionFromSet
    ;(item as any)._doAllowMultipleSelectionsFromSet =
      isParentASet && !!kitProduct.doAllowMultipleSelectionsFromSet
    if (parentOptionUid) {
      ;(item as any)._parentOptionUid = parentOptionUid
    }
  }
  for (const [i, item] of kitItems.entries()) {
    if (!isKitProduct(item.product)) {
      _tagAsOption(item)
      theseProductKitItemsForKitItems.push(item)
      if (!item.isOptional) {
        hasUnconditionalContent = true
      }
    } else {
      console.debug(
        `${fn} Just hit a kit product within a kit (${item.product.descr}).`
      )
      // ⚠ AN OPTIONAL SET OR PLAIN KIT is listed as an option itself -- the
      // same as the supplier-product walk (collectOptionalItemsWithContext) --
      // and the options found inside it depend on it being taken
      // (`_parentOptionUid`, read by cynqueModules/optionSections.ts). A Set
      // held by a Set is one of its alternatives; anything held by a plain Kit
      // is an add-on (a Kit cannot sit in a Set). A Set builds nothing of its
      // own; a Kit builds its header row with everything beneath it.
      // Same uid shape as services/products.ts gives the option.
      const itemUid = `${item.id}__${item.parentProduct?.id || 'root'}`
      const isOption = !!item.isOptional
      if (isOption) {
        _tagAsOption(item)
        theseProductKitItemsForKitItems.push(item)
      }
      const inner = await getProductKitItemsForKitProductDeepWithContent({
        kitProduct: item.product,
        parentOptionUid: isOption ? itemUid : parentOptionUid,
      })
      if (isOption) {
        ;(item as any)._hasContent = inner.hasUnconditionalContent
      } else if (
        !isSetKitProduct(item.product) ||
        inner.hasUnconditionalContent
      ) {
        hasUnconditionalContent = true
      }
      theseProductKitItemsForKitItems.push(...inner.items)
    }
  }
  console.debug(`${fn} returning theseProductKitItemsForKitItems...`)
  console.dir(theseProductKitItemsForKitItems)
  return { items: theseProductKitItemsForKitItems, hasUnconditionalContent }
}

export async function productIsOrContainsProductRecursive({
  product,
  targetProduct,
  kitItemsCache,
}: {
  product: TProduct
  targetProduct: TProduct
  kitItemsCache?: Map<string, IProductKitItem[]>
}) {
  const fn = `${mn} productIsOrContainsProductRecursive |`

  if (!product || !targetProduct) {
    return false
  }
  if (product.id === targetProduct.id) {
    return true
  }
  if (!isKitProduct(targetProduct)) {
    return false
  }

  let kitItems: IProductKitItem[]
  if (kitItemsCache?.has(targetProduct.id)) {
    kitItems = kitItemsCache.get(targetProduct.id)!
  } else {
    kitItems = await getKitItems({
      callingMn: fn,
      product: targetProduct,
    })
    kitItemsCache?.set(targetProduct.id, kitItems)
  }

  for (const item of kitItems) {
    if (item.product.id === product.id) {
      return true
    }
    if (isKitProduct(item.product)) {
      const found = await productIsOrContainsProductRecursive({
        product,
        targetProduct: item.product,
        kitItemsCache,
      })
      if (found) {
        return true
      }
    }
  }
  return false
}
